/**
 * Right pane of the adjudication flow — per `feature-spec.md` §3.5.2.
 *
 * For each dimension where at least one label appears across both
 * annotators, the union is rendered as toggleable chips. Pre-checked
 * via the seed effect in the page; manager curates by clicking chips on
 * or off. Dimensions where neither annotator chose any label are
 * **omitted entirely** (spec §3.5.2).
 *
 * Footer holds rationale textarea + attendees chip (current user only;
 * see plan's "Known gaps") + Resolve & Finalise CTA (Back-to-queue
 * navigation lives in the page-header breadcrumb, not here).
 * button. The button is gated on a non-empty rationale per the spec.
 */
import React, { useMemo } from 'react';

import { Badge } from '../../../../../ui/badge';
import { Button } from '../../../../../ui/button';
import { Textarea } from '../../../../../ui/textarea';
import type { Annotation } from '../../types';
import type { TaxonomyDimension } from '../../../types/taxonomy';

interface Props {
  dimensions: TaxonomyDimension[];
  firstAnnotation: Annotation | null;
  secondAnnotation: Annotation | null;
  resolutionSelections: Record<string, string[]>;
  rationale: string;
  attendees: string[];
  currentUser: { user_id: string; name: string };
  isSubmitting: boolean;
  onToggleLabel: (dimId: string, labelId: string) => void;
  onRationaleChange: (value: string) => void;
  onResolve: () => void;
}

const uniq = (...arrays: string[][]): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const arr of arrays) {
    for (const x of arr) {
      if (!seen.has(x)) {
        seen.add(x);
        out.push(x);
      }
    }
  }
  return out;
};

const ResolutionPane: React.FC<Props> = ({
  dimensions,
  firstAnnotation,
  secondAnnotation,
  resolutionSelections,
  rationale,
  currentUser,
  isSubmitting,
  onToggleLabel,
  onRationaleChange,
  onResolve,
}) => {
  const labelNamesByDim = useMemo<Record<string, Record<string, string>>>(
    () => {
      const out: Record<string, Record<string, string>> = {};
      for (const dim of dimensions) {
        const inner: Record<string, string> = {};
        for (const cat of dim.categories ?? []) {
          for (const label of cat.labels ?? []) {
            inner[label.id] = label.name;
          }
        }
        out[dim.id] = inner;
      }
      return out;
    },
    [dimensions],
  );

  // Build the per-dim union once. Skip dimensions where the union is empty.
  const rows = useMemo(
    () =>
      dimensions
        .map((dim) => {
          const first = firstAnnotation?.labels?.[dim.id] ?? [];
          const second = secondAnnotation?.labels?.[dim.id] ?? [];
          const union = uniq(first, second);
          return { dim, union };
        })
        .filter((row) => row.union.length > 0),
    [dimensions, firstAnnotation, secondAnnotation],
  );

  const canResolve = !isSubmitting && rationale.trim().length > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="resolution-pane">
      <div className="space-y-3">
        {rows.map(({ dim, union }) => {
          const selected = new Set(resolutionSelections[dim.id] ?? []);
          return (
            <div
              key={dim.id}
              className="rounded-lg border border-border bg-background px-4 py-3"
              data-testid={`resolution-dimension-${dim.id}`}
            >
              <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {dim.name}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {union.map((labelId) => {
                  const isSelected = selected.has(labelId);
                  return (
                    <Badge
                      key={labelId}
                      asChild
                      variant={isSelected ? 'default' : 'outline'}
                    >
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        onClick={() => onToggleLabel(dim.id, labelId)}
                        className="cursor-pointer"
                        data-testid={`resolution-chip-${dim.id}-${labelId}`}
                        data-selected={isSelected}
                      >
                        {labelNamesByDim[dim.id]?.[labelId] ?? labelId}
                      </button>
                    </Badge>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-3 border-t border-border pt-4">
        <div className="space-y-1.5">
          <label
            htmlFor="resolution-rationale"
            className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground"
          >
            Resolution rationale
          </label>
          <Textarea
            id="resolution-rationale"
            value={rationale}
            onChange={(e) => onRationaleChange(e.target.value)}
            placeholder="Why these labels? Capture the decision so future audits and re-annotations have context."
            rows={3}
            data-testid="resolution-rationale"
          />
        </div>

        <div className="space-y-1.5">
          <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Attendees
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Badge
              variant="secondary"
              data-testid={`attendee-chip-${currentUser.user_id}`}
            >
              {currentUser.name}
            </Badge>
          </div>
          <p className="text-[11px] italic text-muted-foreground">
            Multi-user attendees picker is coming soon — production gap.
          </p>
        </div>

        <div className="flex items-center justify-end pt-2">
          <Button
            onClick={onResolve}
            disabled={!canResolve}
            data-testid="resolve-finalise"
          >
            {isSubmitting ? 'Resolving…' : 'Resolve & Finalise'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ResolutionPane;
